import { applyGermanize } from './germanize.js';
import { useIsMobile } from './hooks.js';
import { accent, accentBorder, bg, border, borderLight, surface, surface2, textDim, textMuted } from './tokens.js';

const { useState, useEffect } = React;

// ─── About View ──────────────────────────────────────────────────────────
export function AboutView({ onBack, onSelectPattern, onSelectWithMode, onMein }) {
  const mobile = useIsMobile();
  const [hoveredPill, setHoveredPill] = useState(null);
  const labelStyle = {
    fontSize: 11, letterSpacing: 2, textTransform: "uppercase",
    color: accent, marginBottom: 14, fontWeight: 600,
    fontFamily: "'Raleway', system-ui, sans-serif"
  };
  const pStyle = {
    fontSize: mobile ? 15 : 16, color: textMuted,
    lineHeight: 1.7, fontFamily: "Georgia, serif", margin: 0
  };
  const dividerStyle = {
    borderTop: `1px solid ${border}`, margin: "36px 0"
  };

  const modi = [
    { mode: "erkennen", num: "01", title: "Erkennen", text: "Jede Figur mit Beispiel, Ziel, Erkennungsmerkmalen und Reaktionsmoeglichkeiten. Erkennen legt das Fundament: Was ich benennen kann, hat weniger Macht ueber mich." },
    { mode: "unterscheiden", num: "02", title: "Unterscheiden", text: "Dialog-Snippets stellen Figuren nebeneinander, die sich leicht verwechseln lassen. Unterscheiden schaerft den eigenen Blick." },
    { mode: "reagieren", num: "03", title: "Reagieren", text: "Drei Szenarien je Figur – privat, beruflich, oeffentlich. Du formulierst zuerst optional deine eigene Reaktion, dann zeigen wir dir vier Antwortoptionen mit Perspektiven und moeglichen Konsequenzen. Reagieren uebersetzt das Erkannte in den Moment." }
  ];

  const kategorien = [
    { key: "ablenkung",       desc: "verzerrt die Information",         leit: "Was sich vor das Problem stellt, wird selbst zum Thema." },
    { key: "verschiebung",    desc: "veraendert den Rahmen",            leit: "Wer den Rahmen aendert, aendert das Spiel." },
    { key: "destabilisierung",desc: "greift die Wahrnehmung an",        leit: "Wenn das Wahrgenommene wackelt, verstummt die Stimme." },
    { key: "bindung",         desc: "steuern ueber die Beziehung",      leit: "Wer die Beziehung lenkt, lenkt das Gespraech." }
  ];

  return (
    <div style={{background: bg, minHeight: "100vh"}}>
      {/* Zurueck-Streifen */}
      <div style={{
        padding: mobile ? "12px 16px" : "14px 24px",
        borderBottom: `1px solid ${border}`
      }}>
        <button onClick={onBack} style={{
          background: "none", border: "none", color: textMuted,
          cursor: "pointer", fontFamily: "inherit", fontSize: 13,
          display: "flex", alignItems: "center", gap: 6, padding: 0
        }}>
          {applyGermanize("← Zurueck")}
        </button>
      </div>

      <main style={{
        maxWidth: 720, margin: "0 auto",
        padding: mobile ? "32px 20px 64px" : "44px 24px 80px"
      }}>

        {/* Logo */}
        <div style={{textAlign: "center", marginBottom: 36}}>
          <svg viewBox="0 0 200 200" width={mobile ? 80 : 100} height={mobile ? 80 : 100} aria-hidden="true">
            <circle cx="100" cy="100" r="80" fill="none" stroke={accent} strokeWidth="3"/>
            <circle cx="100" cy="100" r="50" fill="none" stroke={accent} strokeWidth="2" opacity="0.6"/>
            <circle cx="130" cy="100" r="10" fill={accent}/>
          </svg>
        </div>

        {/* Section 1: Was ist Obscura? */}
        <div>
          <div style={labelStyle}>{applyGermanize("Was ist Obscura?")}</div>
          <p style={pStyle}>
            {applyGermanize("Obscura macht die rhetorischen Figuren sichtbar, die Gespraeche untertunneln – sie wirken unterhalb des Arguments, im Verborgenen. Geordnet sind sie in vier Kategorien, je nachdem, auf was sie abzielen: die Information, den Rahmen, die Wahrnehmung oder die Beziehung selbst.")}
          </p>
          <p style={{...pStyle, marginTop: 16}}>
            {applyGermanize("Schopenhauer hat in seinen 38 Kunstgriffen vorgefuehrt, wie man Recht behaelt, ob man es hat oder nicht (")}
            <em>{applyGermanize("Eristische Dialektik")}</em>
            {applyGermanize(", postum 1864). Obscura kehrt die Frage um: Wie erkenne ich die Figuren – und bleibe bei mir?")}
          </p>
        </div>

        <div style={dividerStyle} />

        {/* Section 2: Theoretische Grundlagen */}
        <div>
          <div style={labelStyle}>{applyGermanize("Theoretische Grundlagen")}</div>
          <p style={pStyle}>
            {applyGermanize("Drei Linien tragen das Format. Kommunikation gelingt nur als dreifache Selektion – Meinen, Mitteilen, Verstehen (Peyn nach Luhmann); an jedem dieser Punkte koennen die Figuren wirken. Jede Beobachtung hat einen blinden Fleck (von Foerster) – genau dort, wo der Blick vorbeigeht, sind sie am Werk. Und jede Aeusserung traegt zwei Ebenen, Inhalt und Beziehung (Bateson) – die meisten Figuren setzen am Rahmen an, die der Bindung & Kontrolle direkt an der Beziehung.")}
          </p>
        </div>

        <div style={dividerStyle} />

        {/* Section 3: Wie funktioniert Obscura? */}
        <div>
          <div style={labelStyle}>{applyGermanize("Wie funktioniert Obscura?")}</div>
          {modi.map(m => (
            <div key={m.num} style={{marginBottom: 22}}>
              <button onClick={() => onSelectWithMode && onSelectWithMode("whataboutism", m.mode)}
                style={{
                  background: "none", border: "none", padding: "0 0 6px", cursor: "pointer",
                  display: "flex", alignItems: "baseline", gap: 10, fontFamily: "inherit"
                }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
                  textTransform: "uppercase", color: accent,
                  fontFamily: "'Raleway', system-ui, sans-serif"
                }}>{m.num}</span>
                <span style={{
                  fontSize: 14, fontWeight: 600, color: "#e8e6e3",
                  fontFamily: "'Raleway', system-ui, sans-serif"
                }}>{applyGermanize(m.title)}</span>
              </button>
              <p style={{...pStyle, fontSize: mobile ? 14 : 15}}>
                {applyGermanize(m.text)}
              </p>
            </div>
          ))}
        </div>

        <div style={dividerStyle} />

        {/* Section 4: Die 4 Kategorien */}
        <div>
          <div style={{...labelStyle, marginBottom: 20}}>{applyGermanize("Die 4 Kategorien")}</div>
          {kategorien.map((k, i) => {
            const g = GROUPS[k.key];
            if (!g) return null;
            return (
              <div key={k.key} style={{marginBottom: i < kategorien.length - 1 ? 28 : 0}}>
                <div style={{
                  fontSize: 15, fontWeight: 600, color: "#e8e6e3",
                  fontFamily: "'Raleway', system-ui, sans-serif", marginBottom: 4
                }}>{applyGermanize(g.label)}</div>
                <p style={{
                  ...pStyle, fontSize: mobile ? 14 : 15, marginBottom: 4
                }}>{applyGermanize(k.desc)}</p>
                <p style={{
                  ...pStyle, fontSize: mobile ? 13 : 14, color: textDim,
                  fontStyle: "italic", marginBottom: 10
                }}>{applyGermanize(k.leit)}</p>
                <div style={{display: "flex", flexWrap: "wrap", gap: 6}}>
                  {g.members.map(id => {
                    const p = PATTERNS[id];
                    if (!p) return null;
                    return (
                      <div key={id} style={{position: "relative"}}>
                        <button onClick={() => onSelectPattern && onSelectPattern(id)}
                          style={{
                            background: surface, border: `1px solid ${border}`,
                            borderRadius: 999, padding: "5px 12px",
                            color: textMuted, fontSize: 12,
                            cursor: "pointer", fontFamily: "inherit",
                            transition: "color 0.15s, border-color 0.15s"
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.color = accent;
                            e.currentTarget.style.borderColor = accentBorder;
                            setHoveredPill(id);
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.color = textMuted;
                            e.currentTarget.style.borderColor = border;
                            setHoveredPill(null);
                          }}>
                          {applyGermanize(p.title)}
                        </button>
                        {hoveredPill === id && p.headline && (
                          <div style={{
                            position: "absolute", bottom: "calc(100% + 7px)", left: "50%",
                            transform: "translateX(-50%)", background: surface2,
                            border: `1px solid ${borderLight}`, borderRadius: 6,
                            padding: "5px 10px", fontSize: 12, color: "#e8e6e3",
                            whiteSpace: "nowrap", zIndex: 20, pointerEvents: "none",
                            fontFamily: "'Raleway', system-ui, sans-serif"
                          }}>
                            {applyGermanize(p.headline)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div style={dividerStyle} />

        {/* Section: Mein Bereich */}
        <div>
          <div style={{...labelStyle, marginBottom: 20}}>{applyGermanize("Mein Bereich")}</div>
          {[
            { tab: "erkundung", num: "01", title: "Erkundungsreise", text: "Alle 43 Figuren auf einen Blick, geordnet nach den vier Kategorien. Die Erkundungsreise zeigt, welche Muster du schon angesehen und welche du vertieft hast – und macht neugierig auf die, die noch vor dir liegen." },
            { tab: "lernpfade", num: "02", title: "Lernpfade", text: "Lernpfade fuehren entlang einer Leitfrage durch mehrere Figuren, oft quer durch die Kategorien. Geh die kuratierten Routen – oder stell dir eigene zusammen." },
            { tab: "landkarte", num: "03", title: "Landkarte", text: "Die Landkarte zeigt das Netz dahinter: welche Figuren sich leicht verwechseln lassen und wo Bruecken zwischen den Kategorien verlaufen. Antippen und erkunden." }
          ].map(s => (
            <div key={s.tab} style={{marginBottom: 22}}>
              <button onClick={() => onMein && onMein(s.tab)}
                style={{
                  background: "none", border: "none", padding: "0 0 6px", cursor: "pointer",
                  display: "flex", alignItems: "baseline", gap: 10, fontFamily: "inherit"
                }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
                  textTransform: "uppercase", color: accent,
                  fontFamily: "'Raleway', system-ui, sans-serif"
                }}>{s.num}</span>
                <span style={{
                  fontSize: 14, fontWeight: 600, color: "#e8e6e3",
                  fontFamily: "'Raleway', system-ui, sans-serif"
                }}>{applyGermanize(s.title)}</span>
              </button>
              <p style={{...pStyle, fontSize: mobile ? 14 : 15}}>
                {applyGermanize(s.text)}
              </p>
            </div>
          ))}
        </div>

        <div style={dividerStyle} />

        {/* Section 5: Ein Hinweis */}
        <div>
          <div style={labelStyle}>{applyGermanize("Ein Hinweis")}</div>
          <p style={pStyle}>
            {applyGermanize("Ob eine Figur vorliegt, entscheidet, wer sie erlebt – Wahrnehmung wird konstruiert, sie ist kein Spiegel. Dasselbe Verhalten kann Nebelkerze sein oder legitime Vereinfachung. Obscura bietet Orientierung statt Urteilen.")}
          </p>
        </div>

        {/* Footer */}
        <div style={{borderTop: `1px solid ${border}`, marginTop: 40, paddingTop: 28, textAlign: "center"}}>
          <p style={{fontSize: mobile ? 12 : 13, color: textDim, lineHeight: 1.7, maxWidth: 560, margin: "0 auto 20px", fontFamily: "Georgia, serif"}}>
            {applyGermanize("Die Inhalte dieser Seite sind KI-generiert und redaktionell ueberarbeitet. Die Beispiel-Dialoge sind erfunden und veranschaulichen das jeweilige Muster. Obscura unterstuetzt Bildung und Selbstreflexion – Orientierung, keine Beratung und keine Diagnose.")}
          </p>
          <div style={{fontSize: 13, color: textMuted, marginBottom: 12, fontFamily: "'Raleway', system-ui, sans-serif"}}>
            Crafted with <span style={{color: accent}}>♥</span> by <a href="https://www.marcelbinzenhoefer.de/" target="_blank" rel="noopener noreferrer" style={{color: "#e8e6e3", fontWeight: 600, textDecoration: "none"}}>Marcel Binzenhöfer</a> & Claude Code
          </div>
          <div style={{display: "flex", justifyContent: "center", gap: 32}}>
            <a href="https://www.marcelbinzenhoefer.de/impressum" target="_blank" rel="noopener noreferrer" style={{fontSize: 13, color: textMuted, textDecoration: "none", fontWeight: 600, fontFamily: "'Raleway', system-ui, sans-serif"}}>Impressum</a>
            <a href="https://www.marcelbinzenhoefer.de/datenschutz" target="_blank" rel="noopener noreferrer" style={{fontSize: 13, color: textMuted, textDecoration: "none", fontWeight: 600, fontFamily: "'Raleway', system-ui, sans-serif"}}>Datenschutz</a>
          </div>
        </div>

      </main>
    </div>
  );
}
